import {
  AccountType,
  AlertType,
  ConstantsNameDescriptions,
  EncounterTimeFilter,
  GoalType,
  LibraryItemType,
  SpecializationName,
  TargetSideType,
  TherapyType,
} from '_api/types';
import { DateRangeType, RegimenType, ThemeModeType } from '_constants';
import { AlertMessageSeverity } from 'components/molecules/types';

export const ACCOUNT_TYPE: Record<string, AccountType> = {
  ADMIN: 'ADMIN',
  ORG_ADMIN: 'ORGANIZATION_ADMIN',
  HOS_ADMIN: 'HOSPITAL_ADMIN',
  THERAPIST: 'THERAPIST',
  GUARDIAN: 'GUARDIAN',
};

export const AccountTypeLookUp: { type: AccountType; value: string }[] = [
  {
    type: 'ADMIN',
    value: 'Admin',
  },
  {
    type: 'ORGANIZATION_ADMIN',
    value: 'Organization Admin',
  },
  {
    type: 'HOSPITAL_ADMIN',
    value: 'Hospital Admin',
  },
  {
    type: 'THERAPIST',
    value: 'Therapist',
  },
  {
    type: 'GUARDIAN',
    value: 'Guardian',
  },
];

export const SEVERITY: Record<string, AlertMessageSeverity> = {
  error: 'error',
  success: 'success',
};

export const DATERANGE: Record<string, DateRangeType> = {
  week: 'week',
  month: 'month',
  year: 'year',
  notes: 'notes',
};

export const DaysOfWeekOptions: ConstantsNameDescriptions<string>[] = [
  {
    name: 'M',
    description: 'Monday',
  },
  {
    name: 'TU',
    description: 'Tuesday',
  },
  {
    name: 'W',
    description: 'Wednesday',
  },
  {
    name: 'TH',
    description: 'Thursday',
  },
  {
    name: 'F',
    description: 'Friday',
  },
  {
    name: 'SA',
    description: 'Saturday',
  },
  {
    name: 'SU',
    description: 'Sunday',
  },
];

export const DayOfWeekIndex: Record<string, number> = {
  SU: 0,
  M: 1,
  TU: 2,
  W: 3,
  TH: 4,
  F: 5,
  SA: 6,
};

export const ENCOUNTER_TIME_FILTER: Record<string, EncounterTimeFilter> = {
  DAILY: 'daily',
  WEEKLY: 'weekly',
  MONTHLY: 'monthly',
  CUSTOM: 'custom',
};

export const GOALTYPECONSTANTS: Record<string, GoalType> = {
  LONG: 'LONG',
  SHORT: 'SHORT',
};

export const LIBRARYITEMTYPE: Record<string, LibraryItemType> = {
  EXERCISE: 'EXERCISE',
  CHALLENGE: 'CHALLENGE',
  RESOURCE: 'RESOURCE',
};

export const NOTERATING: { [index: number]: string } = {
  1: 'It Was Too Hard',
  2: 'Frustrating',
  3: 'Disappointing',
  4: 'Neutral',
  5: 'Okay',
  6: 'Good',
  7: 'Great',
  8: 'Awesome',
};

export const ALERT_TYPE: Record<string, AlertType> = {
  null: null,
  MISSING: 'MISSING',
  RED: 'RED',
  YELLOW: 'YELLOW',
};

export const alertPriority = (alert: AlertType) => {
  if (alert === ALERT_TYPE.null) {
    return -1;
  } else if (alert === ALERT_TYPE.MISSING) {
    return 0;
  } else if (alert === ALERT_TYPE.YELLOW) {
    return 1;
  } else if (alert === ALERT_TYPE.RED) {
    return 2;
  } else {
    return Number.MIN_SAFE_INTEGER;
  }
};

export const RegimenOptions: ConstantsNameDescriptions<RegimenType>[] = [
  {
    name: 'HABIT',
    description: 'NEW HABIT',
  },
  {
    name: 'REPETITION',
    description: 'REPETITIONS',
  },
  {
    name: 'FATIGUE',
    description: 'TO FATIGUE',
  },
];

export const TherapyTypeOptions: ConstantsNameDescriptions<TherapyType>[] = [
  {
    name: 'SPEECH_THERAPY',
    description: 'SPEECH THERAPY',
  },
  {
    name: 'PHYSICAL_THERAPY',
    description: 'PHYSICAL THERAPY',
  },
  {
    name: 'OCCUPATIONAL_THERAPY',
    description: 'OCCUPATIONAL THERAPY',
  },
];

export const TargetSideOptions: ConstantsNameDescriptions<TargetSideType>[] = [
  {
    name: 'LEFT',
    description: 'Left',
  },
  {
    name: 'RIGHT',
    description: 'Right',
  },
  {
    name: 'MIDDLE',
    description: 'Middle',
  },
  {
    name: 'NA',
    description: 'N/A',
  },
  {
    name: 'BOTH',
    description: 'Both',
  },
];

export const THEMEMODE: Record<string, ThemeModeType> = {
  light: 'light',
  dark: 'dark',
};

export const TherapyTypeValue: Record<TherapyType, string> = {
  SPEECH_THERAPY: 'Speech Therapy',
  PHYSICAL_THERAPY: 'Physical Therapy',
  OCCUPATIONAL_THERAPY: 'Occupational Therapy',
};

export function TherapistSpecToType(type: SpecializationName): TherapyType | undefined {
  if (type === 'OCCUPATIONAL_THERAPIST') return 'OCCUPATIONAL_THERAPY';
  else if (type === 'PHYSICAL_THERAPIST') return 'PHYSICAL_THERAPY';
  else if (type === 'SPEECH_THERAPIST') return 'SPEECH_THERAPY';
  return undefined;
}

export const TherapistSpecializationOptions: ConstantsNameDescriptions<SpecializationName>[] =
  [
    {
      name: 'SPEECH_THERAPIST',
      description: 'SPEECH THERAPIST',
    },
    {
      name: 'PHYSICAL_THERAPIST',
      description: 'PHYSICAL THERAPIST',
    },
    {
      name: 'OCCUPATIONAL_THERAPIST',
      description: 'OCCUPATIONAL THERAPIST',
    },
  ];

export const US_STATES = [
  'AL',
  'AK',
  'AZ',
  'AR',
  'CA',
  'CO',
  'CT',
  'DE',
  'DC',
  'FL',
  'GA',
  'HI',
  'ID',
  'IL',
  'IN',
  'IA',
  'KS',
  'KY',
  'LA',
  'ME',
  'MD',
  'MA',
  'MI',
  'MN',
  'MS',
  'MO',
  'MT',
  'NE',
  'NV',
  'NH',
  'NJ',
  'NM',
  'NY',
  'NC',
  'ND',
  'OH',
  'OK',
  'OR',
  'PA',
  'PR',
  'RI',
  'SC',
  'SD',
  'TN',
  'TX',
  'UT',
  'VT',
  'VA',
  'VI',
  'WA',
  'WV',
  'WI',
  'WY',
];
